import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, User, X } from 'lucide-react';
import { Button, DataTable, Filter, SearchInput } from '@/components';
import { Avatar } from '@/components/ui';
import { UserColumnsSession } from '@/components/session/stepPacient/UserColumnsSession';
import { cn } from '@/lib/utils';
import {
	useGetSession,
	useGetUsers,
	useGetClubs,
	usePatchSessionParticipants,
} from '@/services';
import { useQueryClient } from '@tanstack/react-query';
import { buildPatientFilters } from '@/utils/patientFilters';
import { formatProfessionalFullName } from '@/utils';

/** Resposta do GET /session/:id inclui participants: [{ user_id, user: { id, first_name, last_name } }] */
type SessionWithParticipants = {
	id?: string;
	type?: string;
	professional?: {
		name_prefix?: string | null;
		first_name?: string;
		last_name?: string;
	};
	participants?: Array<{
		user_id?: string;
		user?: { id?: string; first_name?: string; last_name?: string };
	}>;
};

function getParticipantIds(
	session: SessionWithParticipants | null | undefined,
): string[] {
	if (!session?.participants) return [];
	return session.participants
		.map((p) => p.user_id ?? p.user?.id)
		.filter((id): id is string => !!id);
}

function getParticipantNames(
	session: SessionWithParticipants | null | undefined,
): Array<{ id: string; first_name: string; last_name: string }> {
	if (!session?.participants) return [];
	return session.participants
		.map((p) => {
			const id = p.user_id ?? p.user?.id;
			if (!id) return null;
			const u = p.user;
			return {
				id,
				first_name: (u?.first_name ?? '').trim(),
				last_name: (u?.last_name ?? '').trim(),
			};
		})
		.filter(
			(x): x is { id: string; first_name: string; last_name: string } =>
				!!x,
		);
}

const SessionParticipantsPage = () => {
	const { sessionId } = useParams<{ sessionId: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();

	type SelectedDetail = {
		first_name: string;
		last_name: string;
		email?: string;
		profile_photo_url?: string | null;
	};
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
	const [selectedUserDetails, setSelectedUserDetails] = useState<
		Map<string, SelectedDetail>
	>(new Map());
	const lastSyncedSessionId = useRef<string | null>(null);

	// Única fonte: GET /session/:id (o back retorna participants com user_id e user)
	const { data: session, isLoading: sessionLoading } = useGetSession(
		sessionId ?? '',
		{
			enabled: !!sessionId,
			refetchOnMount: 'always',
			staleTime: 0,
		},
	);

	// Sempre que cair na página, forçar refetch da sessão
	useEffect(() => {
		if (!sessionId) return;
		queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
	}, [sessionId, queryClient]);

	// Ao trocar de sessão, resetar para não usar seleção da anterior
	useEffect(() => {
		if (!sessionId) return;
		if (lastSyncedSessionId.current !== sessionId) {
			lastSyncedSessionId.current = null;
		}
	}, [sessionId]);

	// Uma vez que a sessão carregar, preencher seleção a partir de session.participants (só na primeira carga desta sessão)
	useEffect(() => {
		if (!sessionId || !session) return;
		const sess = session as SessionWithParticipants;
		if (sess.id !== sessionId) return;
		if (lastSyncedSessionId.current === sessionId) return;

		lastSyncedSessionId.current = sessionId;
		const ids = getParticipantIds(sess);
		const names = getParticipantNames(sess);
		setSelectedUserIds(ids);
		setSelectedUserDetails(
			new Map(
				names.map((n) => [
					n.id,
					{ first_name: n.first_name, last_name: n.last_name },
				]),
			),
		);
	}, [sessionId, session]);

	const { data: clubsData } = useGetClubs();
	const clubs = Array.isArray(clubsData) ? clubsData : clubsData?.data ?? [];
	const filters = buildPatientFilters(clubs);

	const bannedParams = searchParams.get('banned') as 'true' | 'false';
	const isCompleteParams = searchParams.get('is_complete') as
		| 'true'
		| 'false';
	const clubIdParams = searchParams.get('club_id') || undefined;
	const search = searchParams.get('search');

	const { data: usersData, isFetching } = useGetUsers({
		page,
		limit,
		...(bannedParams && { banned: bannedParams }),
		...(isCompleteParams && { is_complete: isCompleteParams }),
		...(clubIdParams && { club_id: clubIdParams }),
		...(search && { search }),
	});

	useEffect(
		() => setPage(1),
		[bannedParams, isCompleteParams, clubIdParams, search],
	);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleUserSelected = (user: Users.User) => {
		const exists = selectedUserIds.includes(user.id);
		if (exists) {
			setSelectedUserIds((prev) => prev.filter((id) => id !== user.id));
			setSelectedUserDetails((prev) => {
				const next = new Map(prev);
				next.delete(user.id);
				return next;
			});
		} else {
			setSelectedUserIds((prev) => [...prev, user.id]);
			setSelectedUserDetails((prev) =>
				new Map(prev).set(user.id, {
					first_name: user.first_name ?? '',
					last_name: user.last_name ?? '',
					email: user.email,
					profile_photo_url: user.profile_photo_url ?? undefined,
				}),
			);
		}
	};

	const handleRemoveParticipant = (id: string) => {
		setSelectedUserIds((prev) => prev.filter((x) => x !== id));
		setSelectedUserDetails((prev) => {
			const next = new Map(prev);
			next.delete(id);
			return next;
		});
	};

	const handleDeselectAll = () => {
		setSelectedUserIds([]);
		setSelectedUserDetails(new Map());
	};

	const { mutate: patchParticipants, isPending: isSaving } =
		usePatchSessionParticipants(() => {
			navigate('/admin/consultas');
		});

	const handleSave = () => {
		if (!sessionId) return;
		patchParticipants({
			id: sessionId,
			body: { user_ids: selectedUserIds },
		});
	};

	const sess = session as SessionWithParticipants | undefined;
	const initialIds = useMemo(() => getParticipantIds(sess), [sess]);
	const hasChanges =
		initialIds.length !== selectedUserIds.length ||
		initialIds.some((id) => !selectedUserIds.includes(id)) ||
		selectedUserIds.some((id) => !initialIds.includes(id));

	// Lista para os cards: id + dados (nome, email, foto). Dados vêm do GET (sess) ou do Map (quem foi add na tabela).
	const selectedCardsList = useMemo(() => {
		const fromSession = getParticipantNames(sess);
		const byId = new Map(
			fromSession.map((n) => [
				n.id,
				{ first_name: n.first_name, last_name: n.last_name },
			]),
		);
		selectedUserDetails.forEach((v, id) => {
			byId.set(id, { first_name: v.first_name, last_name: v.last_name });
		});
		const detailsById = new Map<string, SelectedDetail>();
		selectedUserDetails.forEach((v, id) => detailsById.set(id, v));
		return selectedUserIds.map((id) => {
			const fromSessionEntry = byId.get(id);
			const detail = detailsById.get(id);
			const first =
				detail?.first_name ?? fromSessionEntry?.first_name ?? '';
			const last = detail?.last_name ?? fromSessionEntry?.last_name ?? '';
			return {
				id,
				first_name: first,
				last_name: last,
				fullName: `${first} ${last}`.trim() || '—',
				email: detail?.email,
				profile_photo_url: detail?.profile_photo_url,
			};
		});
	}, [sess, selectedUserIds, selectedUserDetails]);

	if (!sessionId) {
		navigate('/admin/consultas');
		return null;
	}

	if (sessionLoading && !session) {
		return (
			<div className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:p-8">
				<p className="text-sm font-medium text-stone-500">
					Carregando sessão...
				</p>
			</div>
		);
	}

	if (sess?.type !== 'GROUP') {
		navigate('/admin/consultas');
		return null;
	}

	const sessionLabel = sess?.professional
		? `Sessão em grupo · ${formatProfessionalFullName(sess.professional)}`
		: 'Participantes da sessão';

	return (
		<div className="animate-in fade-in slide-in-from-right-4 mx-auto max-w-7xl space-y-6 px-4 py-6 duration-300 md:space-y-8 md:p-8">
			<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
				<div className="flex items-center gap-4 md:gap-6">
					<button
						type="button"
						onClick={() => navigate('/admin/consultas')}
						className="group flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200 hover:shadow-sm"
					>
						<ArrowLeft
							size={20}
							className="text-stone-400 group-hover:text-stone-900"
						/>
					</button>
					<div className="min-w-0 space-y-0.5">
						<h1 className="truncate text-2xl font-black tracking-tight text-stone-900">
							{sessionLabel}
						</h1>
						<div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-300">
							<span>CONSULTAS</span>
							<span className="text-stone-200">/</span>
							<span className="text-stone-900">
								PARTICIPANTES DA SESSÃO
							</span>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full flex-col gap-5">
				<div className="w-full min-w-0">
					<SearchInput
						placeholder="Pesquisar por nome..."
						className="w-full"
					/>
				</div>
				<div className="w-full min-w-0">
					<Filter filters={filters} />
				</div>
			</div>

			{usersData && (
				<div className="flex w-full flex-col gap-4">
					{usersData.data.length > 0 ? (
						<>
							<DataTable
								columns={UserColumnsSession({
									onSelect: handleUserSelected,
									selectedUserIds,
									variant: 'participantsOnly',
									clubs,
								})}
								data={usersData.data}
								selectedItems={selectedUserIds}
								page={page}
								limit={limit}
								totalItems={usersData.total}
								onPageChange={setPage}
								onLimitChange={handleLimitChange}
							/>

							{selectedUserIds.length > 0 && (
								<div className="flex w-full flex-col overflow-hidden rounded-2xl border border-solid border-stone-200 bg-white shadow-sm">
									{/* Linha "Total selecionado" no estilo da tabela, largura total */}
									<div className="flex items-center justify-between border-b border-stone-200 bg-stone-50/50 px-6 py-4">
										<span className="text-[10px] font-black uppercase tracking-widest text-stone-400">
											Total selecionado:{' '}
											{selectedUserIds.length}
										</span>
										<button
											type="button"
											onClick={handleDeselectAll}
											className="rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-200/60 hover:text-stone-700"
											aria-label="Desmarcar todos"
										>
											<X size={18} strokeWidth={2.5} />
										</button>
									</div>
									{/* Cardzinhos: paleta do projeto (zinc/stone) */}
									<div className="flex flex-wrap gap-2 p-4">
										{selectedCardsList.map((card, idx) => {
											const paletteBg = [
												'bg-zinc-100',
												'bg-stone-100',
												'bg-zinc-50',
											][idx % 3];
											return (
												<div
													key={card.id}
													className={cn(
														'flex items-center gap-2 rounded-xl border border-stone-200/80 px-3 py-2 shadow-sm',
														paletteBg,
													)}
												>
													{card.profile_photo_url ? (
														<Avatar
															className="h-7 w-7 shrink-0 rounded-full"
															variant="circle"
															img_url={
																card.profile_photo_url
															}
														/>
													) : (
														<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-200/80 text-[10px] font-bold uppercase text-stone-600">
															{[
																card.first_name.trim()[0],
																card.last_name.trim()[0],
															]
																.filter(Boolean)
																.join('') ||
																'?'}
														</div>
													)}
													<span className="max-w-[120px] truncate text-xs font-bold text-stone-800">
														{card.fullName}
													</span>
													<button
														type="button"
														onClick={() =>
															handleRemoveParticipant(
																card.id,
															)
														}
														className="-mr-0.5 rounded-lg p-1 text-stone-400 transition-colors hover:bg-stone-200/60 hover:text-stone-700"
														aria-label="Remover participante"
													>
														<X
															size={14}
															strokeWidth={2.5}
														/>
													</button>
												</div>
											);
										})}
									</div>
								</div>
							)}
						</>
					) : (
						!isFetching && (
							<div className="flex w-full flex-col items-center justify-center rounded-3xl border-2 border-dashed border-stone-200 bg-stone-50/50 py-20">
								<User className="mb-3 size-12 text-stone-300" />
								<p className="text-[11px] font-black uppercase tracking-widest text-stone-500">
									Nenhum usuário encontrado
								</p>
							</div>
						)
					)}
				</div>
			)}

			<div className="flex flex-wrap items-center justify-end gap-4 border-t border-stone-100 pt-4">
				<button
					type="button"
					onClick={() => navigate('/admin/consultas')}
					className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600"
				>
					Cancelar
				</button>
				<Button
					text={isSaving ? 'Salvando...' : 'Salvar alterações'}
					onClick={handleSave}
					disabled={!hasChanges || isSaving}
					className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800 disabled:opacity-50"
				/>
			</div>
		</div>
	);
};

export { SessionParticipantsPage };
